// DESIGN-SPEC §5.15 Toast 功能反馈
"use client";

import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useRef, useState } from "react";
import { Button } from "./button";

interface ToastContextValue {
  toast: (message: string) => void;
  message: string | null;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const toast = useCallback((nextMessage: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setMessage(nextMessage);
    timerRef.current = setTimeout(() => setMessage(null), 2_000);
  }, []);

  return (
    <ToastContext.Provider value={{ toast, message }}>
      {children}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast 必须在 ToastProvider 内使用");
  return context.toast;
}

export function Toaster() {
  const context = useContext(ToastContext);
  if (!context) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={
        "pointer-events-none fixed bottom-[104px] left-1/2 z-[99] max-w-[min(88%,420px)] -translate-x-1/2 rounded-full border border-paper-inverse/15 bg-forest-900/94 px-5 py-3 text-center text-[13px] leading-5 font-medium text-paper-inverse shadow-[0_16px_40px_rgba(16,40,27,0.28)] backdrop-blur-xl motion-safe:transition-[opacity,transform] motion-safe:duration-200 " +
        (context.message
          ? "translate-y-0 opacity-100"
          : "translate-y-2 opacity-0")
      }
    >
      {context.message ?? ""}
    </div>
  );
}

export function ToastDemoButton() {
  const toast = useToast();
  return (
    <Button variant="brand" onClick={() => toast("批次信息已复制")}>
      触发 Toast
    </Button>
  );
}
